/**
 * Navigation Component
 * Top-level navigation menu with responsive design
 * 
 * Requirements: 4.1, 10.4
 */

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GradientText } from '@/components/text/gradient-text';
import './Navigation.css';

export interface NavigationProps {
  onLogout?: () => void;
  creditsRemaining?: number | null;
  isLoadingCredits?: boolean;
  subscription?: { subscription_type: string } | null;
  isAdmin?: boolean;
}

function Navigation({ onLogout, creditsRemaining, isLoadingCredits = false, subscription, isAdmin = false }: NavigationProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string): boolean => {
    if (path === '/home') {
      return location.pathname === '/home' || location.pathname === '/';
    }
    return location.pathname === path;
  };

  const hasDataDashboardAccess = (): boolean => {
    if (!subscription) return false;
    const tier = subscription.subscription_type.toLowerCase();
    return ['business', 'platinum', 'enterprise'].includes(tier);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    closeMobileMenu();
    if (onLogout) {
      onLogout();
    }
  };

  // Handle Escape key to close mobile menu
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileMenuOpen]);

  // Handle tutorial system requesting mobile menu close
  useEffect(() => {
    const handleCloseMobileMenu = () => {
      if (isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    window.addEventListener('closeMobileMenu', handleCloseMobileMenu);
    return () => {
      window.removeEventListener('closeMobileMenu', handleCloseMobileMenu);
    };
  }, [isMobileMenuOpen]);

  return (
    <nav className="navigation" role="navigation" aria-label="Main navigation">
      <div className="nav-container">
        {/* Logo/Brand */}
        <div className="nav-brand">
          <Link to="/home" className="nav-logo" onClick={closeMobileMenu}>
            <GradientText
              text="SIFT"
              gradient="linear-gradient(90deg, #3b82f6 0%, #a855f7 50%, #ec4899 100%)"
            />
          </Link>
        </div>

        {/* Credits Counter - Mobile (between logo and hamburger) */}
        {!isLoadingCredits && creditsRemaining !== null && (
          <Link to="/account" className="nav-credits-counter nav-credits-mobile-header" title="View account and manage credits">
            <div className="nav-credits-icon">
              🎫
            </div>
            <div className="nav-credits-info">
              <span className="nav-credits-count">{creditsRemaining}</span>
              <span className="nav-credits-label">credits</span>
            </div>
          </Link>
        )}

        {/* Hamburger menu button for mobile */}
        <button
          className="nav-hamburger"
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Navigation links */}
        <div className={`nav-links ${isMobileMenuOpen ? 'nav-links-open' : ''}`}>
          {/* Credits Counter - Desktop only */}
          {!isLoadingCredits && creditsRemaining !== null && (
            <Link to="/account" className="nav-credits-counter nav-credits-desktop" title="View account and manage credits">
              <div className="nav-credits-icon">
                🎫
              </div>
              <div className="nav-credits-info">
                <span className="nav-credits-count">{creditsRemaining}</span>
                <span className="nav-credits-label">credits</span>
              </div>
            </Link>
          )}
          
          <Link
            to="/home"
            className={`nav-link ${isActive('/home') ? 'nav-link-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Home
          </Link>
          <Link
            to="/categories"
            className={`nav-link ${isActive('/categories') ? 'nav-link-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Categories
          </Link>
          {hasDataDashboardAccess() && (
            <Link
              to="/dashboard"
              className={`nav-link ${isActive('/dashboard') ? 'nav-link-active' : ''}`}
              onClick={closeMobileMenu}
            >
              Dashboard
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/admin"
              className={`nav-link ${isActive('/admin') ? 'nav-link-active' : ''}`}
              onClick={closeMobileMenu}
            >
              System Overview
            </Link>
          )}
          <Link
            to="/account"
            className={`nav-link ${isActive('/account') ? 'nav-link-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Account
          </Link>
          
          {/* Logout button */}
          {onLogout && (
            <button
              className="nav-link nav-logout"
              onClick={handleLogout}
            >
              Logout
            </button>
          )}

          {/* Mobile-only branding at bottom */}
          <div className="nav-mobile-branding">
            <div className="nav-mobile-logo-content">
              <GradientText
                text="SIFT"
                gradient="linear-gradient(90deg, #3b82f6 0%, #a855f7 50%, #ec4899 100%)"
              />
              <span className="nav-powered-by">powered by</span>
              <a 
                href="https://trialitylabs.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="nav-mobile-triality-link"
              >
                <img 
                  src="/assets/trialitylabs_logo.png" 
                  alt="Triality Labs" 
                  className="nav-mobile-triality-logo"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="nav-overlay"
          onClick={closeMobileMenu}
          aria-hidden="true"
        ></div>
      )}
    </nav>
  );
}

export default Navigation;
